import qs from "qs";

import ApiClient, { ApiClientPromiseResponse, PageableFilterRequest } from "services/ApiClient";
import { PagedData } from "types";
import { ProjectDetails, ProjectDetailSiteContact, ProjectListItem } from "types/project";
import { formatSortField } from "utils/FormatUtils";

const PAGE_SIZE = 10;

export type ProjectListQueryParams =
  { page: number, pageSize: number, sortBy?: any, searchFilter?: string }

class ProjectService extends ApiClient {
  getProjectData(id: string): ApiClientPromiseResponse<ProjectDetails> {
    return this.call<ProjectDetails>({
      method: "get",
      url: `/projects/${id}`,
    });
  }

  generateSeedReportNotes(id: string) {
    return this.call({
      method: "put",
      url: "/seed/generate-report-notes",
      data: { id },
    });
  }

  // Example: /projects?Page=1&PageSize=10&Filter=qwer&SortFieldName=name&SortDirection=Descending
  getProjectsList({ page = 1, pageSize = PAGE_SIZE, sortBy, searchFilter }: ProjectListQueryParams): ApiClientPromiseResponse<PagedData<ProjectListItem>> {
    const query: PageableFilterRequest & { name?: string } = {
      page,
      pageSize,
    };

    if (sortBy && sortBy.field && sortBy.direction !== "None") {
      query.sortFieldName = formatSortField(sortBy.field);
      query.sortDirection = sortBy.direction;
    }

    if (searchFilter) {
      query.name = searchFilter;
    }

    return this.call<PagedData<ProjectListItem>>({
      method: "get",
      url: `/projects?${qs.stringify(query)}`,
    });
  }

  getProjectForSC(id: string): ApiClientPromiseResponse<ProjectDetailSiteContact> {
    return this.call<ProjectDetailSiteContact>({
      method: "get",
      url: `/projects/${id}/site-contact`,
    });
  }

  openProject(id: string, data: { reopeningReason: string; }): ApiClientPromiseResponse<{ id: string; }> {
    return this.call<{ id: string; }>({
      method: "patch",
      url: `/projects/${id}/open`,
      data,
    });
  }

  getProjectsCountLimits() {
    return this.call({
      method: "get",
      url: "/projects/count-limits",
    });
  }

  addNewProject(projectData: any) {
    return this.call({
      method: "post",
      url: "/projects",
      data: {
        ...projectData,
      },
    });
  }

  updateProject(projectData: any) {
    return this.call({
      method: "put",
      url: "/projects",
      data: {
        ...projectData,
      },
    });
  }

  updateProjectDetails(id: string, description: string) {
    return this.call({
      method: "put",
      url: `/projects/${id}/description`,
      data: {
        description,
      },
    });
  }

  // Deprecated, should be removed later
  // updatePolicyDate(id, policyStartDate) {
  //   return this.call({
  //     method: 'put',
  //     url: `/projects/${id}/policy-start-date`,
  //     data: {
  //       policyStartDate,
  //     },
  //   });
  // }

  deleteProject(id: string) {
    return this.call({
      method: "delete",
      url: `/projects/${id}`,
      data: {
        id,
      },
    });
  }

  getParentCompanies() {
    return this.call({
      method: "get",
      url: "/parent-companies",
    });
  }

  getProjectBusinessTypes() {
    return this.call({
      method: "get",
      url: "/project-business-types",
    });
  }

  getProjectTypes() {
    return this.call({
      method: "get",
      url: "/project-types",
    });
  }

  addParentCompany(name: string) {
    return this.call({
      method: "post",
      url: "/parent-companies",
      data: {
        name,
      },
    });
  }

  addProjectType(value: any) {
    return this.call({
      method: "post",
      url: "/project-types",
      data: {
        value,
      },
    });
  }

  addProjectBusinessType(projectTypeId: string, value: any) {
    return this.call({
      method: "post",
      url: "/project-business-types",
      data: {
        projectTypeId,
        value,
      },
    });
  }

  editParentCompany(id: string, name: string) {
    return this.call({
      method: "put",
      url: "/parent-companies",
      data: {
        id,
        name,
      },
    });
  }

  editProjectType(id: string, value: any) {
    return this.call({
      method: "put",
      url: "/project-types",
      data: {
        id,
        value,
      },
    });
  }

  editProjectBusinessType(id: string, value: any) {
    return this.call({
      method: "put",
      url: "/project-business-types",
      data: {
        id,
        value,
      },
    });
  }

  getProjectTemplate() {
    return this.call({
      method: "get",
      url: "/projects/import/template",
    });
  }

  importProjects(file: any, onUploadProgress: any) {
    const formDataFile = new FormData();

    formDataFile.append("template", file);

    return this.call({
      method: "POST",
      url: "/projects/import",
      data: formDataFile,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  }

  setPrimaryUnderwriter(id: string, email: string) {
    return this.call({
      method: "put",
      url: `/projects/${id}/primary-underwriter`,
      data: { email },
    });
  }

  getPrimaryUnderwriter(id: string) {
    return this.call({
      method: "get",
      url: `/projects/${id}/primary-underwriter`,
    });
  }

  getPrimaryRiskEngineer(id: string) {
    return this.call({
      method: "get",
      url: `/projects/${id}/primary-risk-engineer`,
    });
  }

  setPrimarySiteContact(id: string, email: string) {
    return this.call({
      method: "put",
      url: `/projects/${id}/primary-site-contact`,
      data: { email },
    });
  }

  getIsReadyForReallocation(id: string) {
    return this.call({
      method: "get",
      url: `/projects/${id}/is-ready-for-reallocation`,
    });
  }

  tabAccess(projectId: string) {
    return this.call({
      method: "get",
      url: `/projects/${projectId}/tab-access`,
    });
  }

  updateProjectOpFees(projectId: string, data: any) {
    return this.call({
      method: "put",
      url: `/projects/${projectId}/operational-var-fees`,
      data,
    });
  }

  isDevicesTabAvailable(projectId: string): ApiClientPromiseResponse<boolean> {
    return this.call<boolean>({
      method: "get",
      url: `/report-notes/is-devices-tab-available?${qs.stringify({ projectId })}`,
    });
  }
}

export default new ProjectService();
